import React, { useState, useEffect, useContext } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
// @ts-ignore
import Cookies from 'js-cookie';
import throttle from 'lodash.throttle';
import { i18nFunct } from '@lucid/gatsby-theme-lucid';
// @ts-ignore
import logo from '../../assets/images/logo-white.svg';

import NavbarLink, { LinkInterface } from '../navbar-link/navbar-link';
import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';
import { useSiteMetadata } from '../../hooks/use-site-metadata';

import './navbar.scss';
import './clear.scss';
import './clear-mobile.scss';
import './clear-white.scss';
import './white.scss';
import './orange.scss';
import './dark.scss';

export const navbarThemes = {
  orange: 'navbar--orange',
  white: 'navbar--white',
  clear: 'navbar--clear',
  dark: 'navbar--dark',
  clearMobile: 'navbar--clear-mobile',
  clearWhite: 'navbar--clear-white'
};

interface Navbar {
  noTopMargin?: boolean;
  noTopMarginMobile?: boolean;
  scrollDistance?: number;
  theme: string;
  menu: LinkInterface[];
  isHomepage?: boolean;
  darkButtons?: boolean;
  hideLinks?: boolean;
  hideSignUpButton?: boolean;
  alwaysScrolled?: boolean;
  isVisio?: boolean;
  isDiscovery?: boolean;
  isStickyMobile?: boolean;
  alternateButtonStyles?: boolean;
}

const Navbar = ({
  noTopMargin,
  noTopMarginMobile,
  scrollDistance,
  theme,
  menu,
  darkButtons,
  hideLinks,
  hideSignUpButton,
  alwaysScrolled,
  isVisio,
  isDiscovery,
  isStickyMobile,
  alternateButtonStyles
}: Navbar) => {
  const { title, productUrl } = useSiteMetadata();
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_user_settings
                mktg_nav_log_out
                mktg_nav_documents
                mktg_nav_sign_up_free
                mktg_nav_log_in
                mktg_nav_menu
                mktg_nav_account
              }
            }
          }
        }
      }
    `
  );
  const isBrowser = typeof window !== 'undefined';
  const username = isBrowser
    ? window.atob(Cookies.get('username') || '')
    : undefined;

  const placeholderButtons: LinkInterface[] = [
    {
      label: '',
      url: '#1',
      type: theme === 'navbar--dark' ? 'primary' : 'secondary',
      fieldKmEventName: ''
    },
    {
      label: '',
      url: '#2',
      type: theme === 'navbar--dark' ? 'secondary' : 'primary',
      fieldKmEventName: ''
    }
  ];

  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState('');
  const [buttons, setButtons] = useState(placeholderButtons);

  const {
    language: { langPrefix, urlLangPrefix, i18nKey }
  } = useContext<LayoutContextProps>(LayoutContext);

  const updateButtons = () => {
    if (username) {
      loggedInButtons[1].label = username;
      setButtons(loggedInButtons);
    } else {
      setButtons(loggedOutButtons);
    }
  };

  useEffect(() => {
    const handleScroll = throttle(() => {
      const distance = scrollDistance || 0;
      setIsScrolled(window.pageYOffset > distance);
    }, 100);

    if (scrollDistance) {
      window.addEventListener('scroll', handleScroll);
    }

    updateButtons();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleSubMenu = (thisOpenSubMenu = '', checkWidth?: boolean) => {
    if (checkWidth && window.outerWidth <= 705) {
      return false;
    }
    setOpenSubMenu(thisOpenSubMenu);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    toggleSubMenu();
    document.body.classList.toggle('lock-scroll');
  };

  const i18n = i18nFunct(i18nData, i18nKey);
  const visioQueryParams = '?type=visio';
  const discoveryQueryParams = '?type=discovery';
  const pricingUrl = `${productUrl}/${urlLangPrefix}users/registerLevel`;

  const pricingUrlQueryParams = () => {
    if (isVisio) {
      return `${pricingUrl}${visioQueryParams}`;
    } else if (isDiscovery) {
      return `${pricingUrl}${discoveryQueryParams}`;
    }
    return pricingUrl;
  };

  const iconHref = langPrefix === 'en' ? '/' : `/${urlLangPrefix}`;

  const loggedInButtons: LinkInterface[] = [
    {
      label: i18n('mktg-nav-documents'),
      url: `${productUrl}/documents`,
      type: theme === 'navbar--dark' ? 'primary' : 'secondary',
      fieldKmEventName: 'documents'
    },
    {
      label: username,
      sublinks: [
        {
          label: i18n('mktg-nav-user-settings'),
          url: `${productUrl}/user/settings`,
          fieldKmEventName: 'settings'
        },
        {
          label: i18n('mktg-nav-log-out'),
          url: `${productUrl}/users/logout`,
          fieldKmEventName: 'logout'
        }
      ],
      type: theme === 'navbar--dark' ? 'secondary' : 'primary',
      fieldKmEventName: 'userMenu',
      truncate: true
    }
  ];

  const loggedOutButtons: LinkInterface[] = [
    {
      label: i18n('mktg-nav-sign-up-free'),
      url: pricingUrlQueryParams(),
      type: 'primary',
      fieldKmEventName: 'signUpFree'
    },
    {
      label: i18n('mktg-nav-log-in'),
      url: `${productUrl}/${urlLangPrefix}users/login`,
      type: 'secondary',
      fieldKmEventName: 'login'
    }
  ];

  if (hideSignUpButton) {
    loggedOutButtons.shift();
  }

  return (
    <>
      {!noTopMargin && (
        <div
          className={`navbar-placeholder ${
            noTopMarginMobile ? 'navbar-placeholder--no-mobile' : ''
          }`}
        />
      )}
      <header
        className={`navbar ${theme} ${isOpen ? 'navbar--open' : ''} ${
          alwaysScrolled || isScrolled ? 'navbar--scrolled' : ''
        } ${isStickyMobile ? 'navbar--mobile-sticky' : ''}`}
        onMouseLeave={() => toggleSubMenu()}
      >
        <nav className="navbar__container">
          <div
            className={`navbar__content ${
              alwaysScrolled || isScrolled
                ? 'navbar__content--border-bottom'
                : ''
            }`}
          >
            <div className="navbar__top-bar">
              <div className="navbar__logo">
                <Link
                  className="navbar__logo-link"
                  to={iconHref}
                  data-km-trigger="nav-logo"
                >
                  <img
                    className="navbar__logo-image"
                    src={logo}
                    alt={title!}
                    width="150"
                  />
                </Link>
              </div>
              <button
                type="button"
                className="navbar__mobile-btn"
                onClick={toggleMenu}
                data-km-trigger="nav-mobile-menu"
              >
                {isOpen ? String.fromCharCode(10005) : i18n('mktg-nav-menu')}
              </button>
            </div>

            <div className="navbar__links-wrap">
              {!hideLinks && (
                <div className="navbar__links">
                  {menu.map(link => (
                    <NavbarLink
                      key={link.url}
                      link={link}
                      isVisio={isVisio}
                      visioQueryParams={visioQueryParams}
                      toggleSubMenu={toggleSubMenu}
                      openSubMenu={openSubMenu}
                      darkButtons={darkButtons}
                    />
                  ))}
                </div>
              )}

              <div
                className={`navbar__buttons ${
                  alternateButtonStyles ? 'navbar__buttons--alternate' : ''
                }`}
              >
                {buttons.map(link => (
                  <NavbarLink
                    key={link.url}
                    link={link}
                    isVisio={isVisio}
                    visioQueryParams={visioQueryParams}
                    useButtons
                    toggleSubMenu={toggleSubMenu}
                    openSubMenu={openSubMenu}
                    darkButtons={darkButtons}
                  />
                ))}
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Navbar;
